export type SidebarReorderPosition = {
  at: 'before' | 'after';
  key: string;
};

export type SidebarReorderPlacement = SidebarReorderPosition & {
  insertionIndex: number;
  isOriginalSlot: boolean;
  scope: string;
  sourceKey: string;
};

export type SidebarReorderRowSnapshot = {
  bottom: number;
  key: string;
  midpoint: number;
  top: number;
};

export type SidebarReorderDragSnapshot = {
  sourceIndex: number;
  sourceKey: string;
  targetRows: SidebarReorderRowSnapshot[];
};

const isPointerInsideRow = (pointerY: number, row: SidebarReorderRowSnapshot) =>
  pointerY >= row.top && pointerY <= row.bottom;

export const getSidebarReorderCommitPosition = (
  placement: SidebarReorderPlacement | null
): SidebarReorderPosition | null => {
  if (!placement || placement.isOriginalSlot) {
    return null;
  }

  return {
    at: placement.at,
    key: placement.key,
  };
};

export const getSidebarReorderOriginalPlacement = ({
  scope,
  snapshot,
}: {
  scope: string;
  snapshot: SidebarReorderDragSnapshot;
}): SidebarReorderPlacement => ({
  at: 'before',
  insertionIndex: snapshot.sourceIndex,
  isOriginalSlot: true,
  key: snapshot.sourceKey,
  scope,
  sourceKey: snapshot.sourceKey,
});

const getSidebarReorderPlacementForInsertionIndex = ({
  insertionIndex,
  scope,
  snapshot,
}: {
  insertionIndex: number;
  scope: string;
  snapshot: SidebarReorderDragSnapshot;
}): SidebarReorderPlacement => {
  if (
    insertionIndex === snapshot.sourceIndex ||
    snapshot.targetRows.length === 0
  ) {
    return getSidebarReorderOriginalPlacement({
      scope,
      snapshot,
    });
  }

  if (insertionIndex < snapshot.targetRows.length) {
    return {
      at: 'before',
      insertionIndex,
      isOriginalSlot: false,
      key: snapshot.targetRows[insertionIndex].key,
      scope,
      sourceKey: snapshot.sourceKey,
    };
  }

  return {
    at: 'after',
    insertionIndex,
    isOriginalSlot: false,
    key: snapshot.targetRows[snapshot.targetRows.length - 1].key,
    scope,
    sourceKey: snapshot.sourceKey,
  };
};

export const getSidebarReorderPlacement = ({
  pointerY,
  scope,
  snapshot,
}: {
  pointerY: number;
  scope: string;
  snapshot: SidebarReorderDragSnapshot;
}): SidebarReorderPlacement | undefined => {
  const firstRow = snapshot.targetRows[0];
  const lastRow = snapshot.targetRows[snapshot.targetRows.length - 1];

  if (firstRow && pointerY < firstRow.top) {
    return getSidebarReorderPlacementForInsertionIndex({
      insertionIndex: 0,
      scope,
      snapshot,
    });
  }

  if (lastRow && pointerY > lastRow.bottom) {
    return getSidebarReorderPlacementForInsertionIndex({
      insertionIndex: snapshot.targetRows.length,
      scope,
      snapshot,
    });
  }

  const hoveredIndex = snapshot.targetRows.findIndex(row =>
    isPointerInsideRow(pointerY, row)
  );
  if (hoveredIndex < 0) {
    return undefined;
  }

  const hoveredRow = snapshot.targetRows[hoveredIndex];
  const insertionIndex =
    pointerY < hoveredRow.midpoint ? hoveredIndex : hoveredIndex + 1;
  return getSidebarReorderPlacementForInsertionIndex({
    insertionIndex,
    scope,
    snapshot,
  });
};
